// Leaf helpers for finding Slot elements inside expressions. Extracted from
// `@packages/utilities/platform` so leaf consumers (notably
// `@packages/systems/component-slots`) can look up Slots without pulling in
// the rest of `platform` and forming an import cycle.
import {
  Component,
  Expressions,
  matchExpression,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  EElement,
  EList,
  EText,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {isSlot} from '@packages/utilities/elementTypeUtils';
import {concat, constant, emptyArray, flip} from '@packages/utilities/fp/utils';

const noSlots = Expressions.resume<ExpressionElement[]>([]);

const getSlotsFromExpr: (expr: Expression) => ExpressionElement[] =
  Expressions.everything(flip(concat))(
    matchExpression({
      Element: (maybeSlotElement) => {
        if (isSlot(maybeSlotElement)) {
          return Expressions.stop([maybeSlotElement]);
        }
        return noSlots;
      },
      _: constant(noSlots),
    })
  );

/**
 * Returns all Slot elements found within given component
 */
export function getComponentSlots(
  component: Component.ComponentType
): ExpressionElement[] {
  return getSlotsFromExpr(Component.getRender(component));
}

/**
 * Returns all Slot elements found within given element
 */
export function getSlots(element: ExpressionElement): ExpressionElement[] {
  return getSlotsFromExpr(EElement(element));
}

// Slot restrictions live in the Slot element's own data under this key, which
// is declared on the Slots atom's `fields` (see
// packages/systems/plugins/Slots/Slot.tsx).
//
// Because it is declared with `defaultValue: []`, reification materializes it
// onto every Slot element -- including ones that have never been restricted --
// so a Slot stored before the declaration existed comes back carrying
// `allowedComponents: []`. That rewrite is the same one SCALE-1515 (#83970)
// took when it added the `slot` field to this atom, and it needs no migration:
// the value is seeded from the declared default, and every read here treats an
// empty list as "unrestricted" (see getSlotRestrictions in SlotElementUtils.ts),
// so a materialized empty list means exactly what an absent key used to.
//
// Writes must therefore persist the empty list rather than delete the key --
// deleting it only invites reification to put it back, leaving two spellings
// of "unrestricted" in stored data.
export const allowedComponentsPath: ExpressionPath = [
  {in: 'Record', at: 'allowedComponents'},
];

/**
 * The component ids a Slot has been restricted to. An empty list means the
 * Slot is unrestricted.
 */
export function getSlotAllowedComponentIds(
  slotElement: ExpressionElement
): string[] {
  const expr = Expressions.getIn(
    Expressions.getElementData(slotElement),
    allowedComponentsPath
  );
  const items = expr ? Expressions.getList(expr) : null;
  if (!items) return emptyArray;

  const ids: string[] = [];
  for (const item of items) {
    const id = Expressions.getText(item);
    if (id) ids.push(id);
  }
  return ids;
}

/**
 * Sets the component ids a Slot is restricted to. An empty list lifts the
 * restriction and is stored as an empty list, matching what reification
 * produces from the field's default.
 */
export function setSlotAllowedComponentIds(
  slotElement: ExpressionElement,
  allowedComponentIds: string[]
): ExpressionElement {
  const data = Expressions.getElementData(slotElement);
  const nextData = Expressions.sparseSetIn(
    data,
    allowedComponentsPath,
    EList(allowedComponentIds.map(EText))
  );
  return Expressions.setElementData(slotElement, nextData);
}

/**
 * Whether any Slot on the component carries an allow-list. The ids in that
 * list are meaningless once exported to a library -- see the library
 * verification check that uses this to block export.
 */
export function hasSlotRestrictions(
  component: Component.ComponentType
): boolean {
  return getComponentSlots(component).some(
    (slot) => getSlotAllowedComponentIds(slot).length > 0
  );
}
